import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { UtilityService } from 'app/shared/services/utility.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { BudgetObjectDialogService } from '../../shared/services/budget-object-dialog.service';
import { Configuration } from 'app/app.constants';
import { ObjectTypeDO, ObjectTypeService } from '../../shared/services/backend/object-type.service';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmationDialogComponent } from '../../shared/components/confirmation-dialog/confirmation-dialog.component';
import { DialogContext } from '../../shared/types/dialog-context.interface';
import * as utils from 'app/shared/utils/notification-dialog.utils';
import { ConfirmType } from '../../shared/utils/notification-dialog.utils';
import { CompanyDataService } from '../../shared/services/company-data.service';
import { BudgetSelectionModalService } from '../../shared/services/budget-selection-modal.service';
import { BudgetSelectionUsageCountService } from '../budget-selection-attributes-tags/budget-selection-attributes-tags.service';

@Component({
  selector: 'tab-campaign-expense-group-type',
  templateUrl: './tab-campaign-expense-group-type.component.html',
  styleUrls: ['./tab-campaign-expense-group-type.component.scss']
})
export class GroupTypeTabComponent implements OnChanges, OnDestroy {
  @Input() companyId: number;
  @Input() budgetId: number;
  readonly destroy$ = new Subject<void>();
  groupTypeList: ObjectTypeDO[];
  appliedSorting = true;
  SearchPlaceHolder = 'Search Campaign and Expense Group Types';
  NoDataText = '';
  
  selectedItemId: number;
  selectedItemName: string;

  public gridColumns  = [
    { name: 'name', label: 'Campaign / Expense Group Type', sortable: true, width: '40%' },
    { name: 'is_enabled', label: 'Enabled', sortable: true, width: '25%' },
    { name: 'usage_count', label: 'Count', sortable: true, width: '20%' }
  ];

  enableSwitchTooltip = this.config.GroupTypeDescriptions.enableTooltip;
  deleteLabel = this.config.GroupTypeDescriptions.deleteLabel;
  deleteConfirmationMessage = this.config.GroupTypeDescriptions.deleteConfirmationMessage;

  constructor(
    private readonly utilityService: UtilityService,
    private readonly service: ObjectTypeService,
    private readonly dialogService: BudgetObjectDialogService,
    private readonly dialog: MatDialog,
    private config : Configuration,
    private companyDataService : CompanyDataService,
    private budgetSelectionModalService: BudgetSelectionModalService,
    private usageService: BudgetSelectionUsageCountService
  ) { 
    this.NoDataText = this.config.attributesAndTagsLabel.CAMPAIGN_TYPE;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.companyId) {
      this.getGroupTypesList(this.companyId);
    }
  }

  getGroupTypesList(companyId: number) {
    this.utilityService.showLoading(true);
    this.service.getNewObjectTypes({ company: companyId })
    .pipe(
      takeUntil(this.destroy$)
    )
    .subscribe(
      data => this.onGroupTypesListLoaded(data),
      error => this.utilityService.handleError(error)
    );
  }
  
  updateStatusChange(data) {
    this.utilityService.showLoading(true);
    this.service.updateObjectType(data.id, data)
    .pipe(
      takeUntil(this.destroy$)
    ).subscribe(
      () => this.onGroupTypeListEvent(this.config.GroupTypeDescriptions.updated),
      error => this.utilityService.handleError(error)
    );
  }

  addGroupType(data) {
    this.utilityService.showLoading(true);
    data.is_custom = true;
    data.company = this.companyId;
    this.service.addObjectType(data)
    .pipe(
      takeUntil(this.destroy$)
    ).subscribe(
      () => this.onGroupTypeListEvent(this.config.GroupTypeDescriptions.created),
      error => this.utilityService.handleError(error)
    );
  }

  updateGroupType(data) {
    this.utilityService.showLoading(true);
    this.service.updateObjectType(data.id, data)
    .pipe(
      takeUntil(this.destroy$)
    ).subscribe(
      () => this.onGroupTypeListEvent(this.config.GroupTypeDescriptions.updated),
      error => this.utilityService.handleError(error)
    );
  }

  deleteGroupType(row) {
    const deleteHandler = () => {
      this.utilityService.showLoading(true);
      this.service.deleteNewWorldObjectType(row.id, {company:this.companyId})
        .pipe(
          takeUntil(this.destroy$)
        ).subscribe(
          () => this.onGroupTypeListEvent(this.config.GroupTypeDescriptions.deleted),
          () => this.showDeleteNotificationDialog(row.name)
        );
    };
    const dialogMessage = `You will permanently lose the '${row.name}' campaign/expense group from everywhere it is used. '${row.name}' will be replaced with 'Other'. `;

    this.dialogService.openDeleteEntityDialog(deleteHandler, null, { title: 'Permanently Delete Campaign/Expense Group?', message: dialogMessage });
  }

  bulkDeleteGroupTypes(e) {
    this.utilityService.showLoading(true);    
    this.service.bulkDeleteObjectTypesWithUsage(this.companyId, e)
      .pipe(
        takeUntil(this.destroy$)
      )
      .subscribe(
        () => this.onGroupTypeListEvent(`${e.length} Campaign / Expense Group Type(s) deleted`),
        error => this.utilityService.handleError(error)
      );
  }


  showDeleteNotificationDialog(groupTypeName: string) {
    const dialogData: DialogContext = utils.getConfirmationDialogData(ConfirmType.DeleteGroupType, groupTypeName);
    this.dialog.open(ConfirmationDialogComponent, {
      width: dialogData.width,
      data: dialogData
    });
  }

  onGroupTypeListEvent(msg : string) {
    this.utilityService.showToast({ Title: '', Message: msg, Type: 'success' });
    this.utilityService.showLoading(false);
    this.getGroupTypesList(this.companyId);
  }

  onGroupTypesListLoaded(data) {
    this.groupTypeList = data;

    const updatedData = data.map(item => ({
      id: item.id,
      name: item.name,
      isCustom: item.is_custom,
      isEnabled: item.is_enabled,
      createdDate: item.crd && new Date(item.crd),
      updatedDate: item.upd && new Date(item.upd),
      companyId: this.companyId
    }));

    this.companyDataService._budgetObjectTypes$.next(updatedData)    
    this.companyDataService.campaignTypes.next(this.companyDataService.currentCampaignTypes = updatedData);
    this.companyDataService.programTypes.next(this.companyDataService.currentProgramTypes = updatedData);
    this.utilityService.showLoading(false);
  }

  actionOnCountEvent(event) {
    this.selectedItemId = event.id;
    this.selectedItemName = event.name;
    this.usageService.getBudgetSelectionUsageCount('object_type', event.id, { company: this.companyId })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: data => this.onBudgetSelectionUsageCountLoaded(data),
        error: error => this.utilityService.handleError(error),
        complete: () => console.log('Budget selection usage count loaded.')
      });
  }
  

  onBudgetSelectionUsageCountLoaded(groups) {
    const data = {
      title: 'View Budget',
      content: `This Campaign / Expense Group Type is included in multiple budgets.  <br/>Select the specific budget from the drop-down menu to view it.`,
      icon: {
        prefix: 'fas',
        name: 'sack-dollar'
      },
      buttons : [
        { text: 'View', color: 'primary', disabled: true }
      ],
      currentBudgetId : this.budgetId,
      selectedItemId : this.selectedItemId,
      selectedItemName : this.selectedItemName,
      selectedTab : 'groupTypes',
      groups : [
        ...groups
      ]
    }
    if (groups.length === 1) {
      data.content = `This Campaign / Expense Group Type is included in <b> ${groups[0].budget_name}</b> budget.`;
      data.buttons[0].disabled = false;
    }
    this.budgetSelectionModalService.openBudgetSelectionDialog(data);
  }
}
